import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { LayoutGrid, KanbanSquare, Sparkles, ChevronRight, ChevronLeft, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const STORAGE_KEY = "sprintroll_onboarding_v1";

/**
 * 3-step tour for brand-new users. Renders as a fixed bottom-right card
 * (non-blocking, so the user can still see the empty state behind it).
 * Persists completion / skip to localStorage.
 */
export default function OnboardingTour({ boardsCount, onCreateBoard }) {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      const done = localStorage.getItem(STORAGE_KEY);
      // Only show for users with 0 boards who haven't dismissed the tour.
      if (!done && boardsCount === 0) setVisible(true);
    } catch {}
  }, [boardsCount]);

  const complete = () => {
    try { localStorage.setItem(STORAGE_KEY, new Date().toISOString()); } catch {}
    setVisible(false);
  };

  if (!visible) return null;

  const steps = [
    {
      icon: Sparkles,
      title: t("onboarding.step1Title", "¡Bienvenido a Sprintroll!"),
      body: t("onboarding.step1Body", "Sprintroll te ayuda a organizar proyectos con tableros visuales tipo Kanban. Te mostramos los básicos en 3 pasos."),
    },
    {
      icon: LayoutGrid,
      title: t("onboarding.step2Title", "Crea tu primer tablero"),
      body: t("onboarding.step2Body", "Un tablero es donde vive tu proyecto. Cada tablero tiene listas (columnas) y tarjetas (tareas). Empieza creando uno."),
      cta: {
        label: t("onboarding.step2Cta", "Crear tablero"),
        onClick: () => { complete(); onCreateBoard?.(); },
      },
    },
    {
      icon: KanbanSquare,
      title: t("onboarding.step3Title", "Organiza con listas y tarjetas"),
      body: t("onboarding.step3Body", "Dentro del tablero, arrastra tarjetas entre listas para reflejar su avance: Por hacer → En progreso → Hecho. Pídele a NadIA (arriba a la derecha) que te sugiera qué hacer hoy."),
    },
  ];

  const s = steps[step];
  const Icon = s.icon;
  const isLast = step === steps.length - 1;
  const isFirst = step === 0;

  return (
    <div
      className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-sm rounded-2xl border border-white/60 dark:border-white/10 bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl shadow-2xl p-5"
      data-testid="onboarding-tour"
      role="dialog"
      aria-label={t("onboarding.aria", "Guía inicial de Sprintroll")}
    >
      <button
        onClick={complete}
        className="absolute top-3 right-3 h-7 w-7 grid place-items-center rounded-lg hover:bg-slate-100 dark:hover:bg-white/10 text-muted-foreground"
        aria-label={t("common.close", "Cerrar")}
        data-testid="onboarding-close"
      ><X className="h-4 w-4" /></button>

      <div className="flex items-center gap-2 mb-3">
        {steps.map((_, i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-all ${
              i === step ? "bg-blue-500" : i < step ? "bg-blue-500/60" : "bg-slate-200 dark:bg-white/10"
            }`}
          />
        ))}
      </div>

      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-blue-500/10 grid place-items-center shrink-0">
          <Icon className="h-5 w-5 text-blue-600 dark:text-blue-300" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
            {t("onboarding.stepLabel", "Paso")} {step + 1}/{steps.length}
          </div>
          <h3 className="font-display text-lg font-semibold mt-0.5" data-testid="onboarding-title">{s.title}</h3>
          <p className="text-sm text-muted-foreground mt-1.5">{s.body}</p>
        </div>
      </div>

      <div className="flex items-center justify-between mt-5 pt-4 border-t border-slate-200/60 dark:border-white/5">
        <button
          onClick={complete}
          className="text-xs text-muted-foreground hover:text-foreground"
          data-testid="onboarding-skip"
        >{t("onboarding.skip", "Omitir tour")}</button>
        <div className="flex items-center gap-2">
          {!isFirst && (
            <Button
              variant="ghost" size="sm" onClick={() => setStep(step - 1)}
              data-testid="onboarding-back"
            ><ChevronLeft className="h-4 w-4 mr-1" /> {t("onboarding.back", "Atrás")}</Button>
          )}
          {s.cta ? (
            <Button
              size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={s.cta.onClick}
              data-testid="onboarding-cta"
            >{s.cta.label}</Button>
          ) : isLast ? (
            <Button
              size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={complete}
              data-testid="onboarding-finish"
            >{t("onboarding.finish", "Empezar")}</Button>
          ) : (
            <Button
              size="sm" className="bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => setStep(step + 1)}
              data-testid="onboarding-next"
            >{t("onboarding.next", "Siguiente")} <ChevronRight className="h-4 w-4 ml-1" /></Button>
          )}
        </div>
      </div>
    </div>
  );
}
